import { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { api, formatApiErrorDetail } from "../lib/api";
import { Button } from "./ui/button";
import { Mail, Loader2, CheckCircle2, ShieldCheck, X } from "lucide-react";
import { toast } from "sonner";

const VERIFIED_SEEN_KEY = "resumelock_email_verified_acknowledged";

export default function VerifyEmailBanner() {
  const { user, refresh } = useAuth();
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  // Re-check verification status on mount so coming back from the verify link updates immediately
  useEffect(() => {
    if (user && user !== false && !user.email_verified) {
      refresh();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (user && user !== false && user.email_verified) {
      try {
        if (localStorage.getItem(VERIFIED_SEEN_KEY) === user.id) {
          setDismissed(true);
        }
      } catch (e) { /* ignore */ }
    }
  }, [user]);

  if (!user || user === false) return null;

  // ----- VERIFIED state -----
  if (user.email_verified) {
    if (dismissed) return null;
    return (
      <div
        className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 flex flex-wrap items-center justify-between gap-3 mb-6"
        data-testid="verify-email-banner-verified"
      >
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center shrink-0">
            <CheckCircle2 className="w-4 h-4" />
          </div>
          <div className="text-sm">
            <div className="font-medium text-emerald-900 flex items-center gap-2">
              Email verified
              <span className="inline-flex items-center gap-1 text-[11px] bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded font-mono-display uppercase tracking-wide">
                <ShieldCheck className="w-3 h-3" /> Secure
              </span>
            </div>
            <div className="text-emerald-800/80">
              <span className="font-medium">{user.email}</span> is confirmed. You can now recover your account by email.
            </div>
          </div>
        </div>
        <Button
          size="sm"
          variant="ghost"
          className="text-emerald-900/70 hover:bg-emerald-100 hover:text-emerald-900"
          onClick={() => {
            try { localStorage.setItem(VERIFIED_SEEN_KEY, user.id); } catch (e) {}
            setDismissed(true);
          }}
          data-testid="banner-verified-dismiss"
        >
          <X className="w-3.5 h-3.5 mr-1" /> Hide
        </Button>
      </div>
    );
  }

  // ----- NOT VERIFIED state -----
  const resend = async () => {
    setLoading(true);
    try {
      await api.post("/auth/resend-verification");
      setSent(true);
      toast.success("Verification email sent");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const checkAgain = async () => {
    setLoading(true);
    try {
      const u = await refresh();
      if (u && u.email_verified) {
        toast.success("Email verified");
      } else {
        toast.message("Still not verified — check your inbox and click the link.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 flex flex-wrap items-center justify-between gap-3 mb-6"
      data-testid="verify-email-banner"
    >
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-lg bg-amber-100 text-amber-700 flex items-center justify-center shrink-0">
          <Mail className="w-4 h-4" />
        </div>
        <div className="text-sm">
          <div className="font-medium text-amber-900">Verify your email address</div>
          <div className="text-amber-800/80">
            We sent a confirmation link to <span className="font-medium">{user.email}</span>. Verifying enables password recovery.
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <Button
          size="sm"
          variant="outline"
          className="border-amber-300 bg-white hover:bg-amber-100 text-amber-900"
          onClick={checkAgain}
          disabled={loading}
          data-testid="banner-recheck-btn"
        >
          {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : "I've verified"}
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={resend}
          disabled={loading || sent}
          className="text-amber-900 hover:bg-amber-100"
          data-testid="banner-resend-btn"
        >
          {loading ? <Loader2 className="w-3 h-3 animate-spin" />
            : sent ? <><CheckCircle2 className="w-3 h-3 mr-1" /> Sent</>
            : "Resend email"}
        </Button>
      </div>
    </div>
  );
}
